import { useState } from "react";
import { themeVar as colors } from "../design-tokens";
import { InfoButton } from "./SubjectControls";
import {
  LOT_CONFLICT_EXPLAIN, candidateLabel, conflictPercent, type LotCandidate,
} from "./lotConflict";

/**
 * THE PICKER FOR A LOT SIZE THE SOURCES DISAGREE ABOUT (2026-11-03, owner spec).
 *
 * Every candidate is shown at once with its source tag and record id, as a native radio group
 * (fieldset + legend, arrow keys move the choice, the browser announces "n of m"). Nothing is
 * preselected: a default would be the silent choice this exists to end. Picking one fills the
 * Lot size row above with that value and its source tag; the row stays editable, and an edit is
 * the agent's own figure ("You entered") with the picked value kept as the original.
 *
 * Pure and controlled: it renders `candidates` and `selected` and reports a pick upward. It does
 * no arithmetic on a figure; the percentage in the sentence comes from `lotConflict`.
 */
export function LotConflictPicker({
  candidates, selected, onPick,
}: {
  candidates: LotCandidate[];
  /** Index of the picked candidate, or null while unresolved (or after the agent typed their own). */
  selected: number | null;
  onPick: (index: number) => void;
}) {
  const [infoOpen, setInfoOpen] = useState(false);
  return (
    <fieldset
      data-lot-conflict
      style={{
        margin: "2px 0 0 32px", padding: "8px 12px 10px", minWidth: 0,
        border: `1px solid ${selected == null ? colors.caution : colors.border}`,
        borderRadius: 8, background: colors.bgSurface,
      }}
    >
      <legend style={{ padding: "0 6px", fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>
        Lot size: the sources differ by {conflictPercent(candidates)}%
        <InfoButton label="the lot size difference" open={infoOpen} panelId="vb-info-lot-conflict"
                    onToggle={() => setInfoOpen((o) => !o)} />
      </legend>
      {infoOpen && (
        <p id="vb-info-lot-conflict" data-info-panel="lot-conflict" role="note"
           style={{ margin: "0 0 6px", fontSize: 12, lineHeight: 1.5, color: colors.textSecondary }}>
          {LOT_CONFLICT_EXPLAIN}
        </p>
      )}
      <p style={{ margin: "0 0 6px", fontSize: 12, lineHeight: 1.5, color: colors.textSecondary }}>
        {selected == null
          ? "Pick the one you trust. Nothing is chosen for you, and the report cannot be built until this is settled."
          : "You can still switch, or correct the figure in the row above."}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {candidates.map((c, i) => {
          const id = `vb-lot-candidate-${i}`;
          return (
            <label key={id} htmlFor={id} data-lot-candidate={i}
                   style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer",
                            fontSize: 13, color: colors.textPrimary, lineHeight: 1.45 }}>
              <input type="radio" id={id} name="vb-lot-candidate" checked={selected === i}
                     onChange={() => onPick(i)} style={{ marginTop: 3 }} />
              <span style={{ minWidth: 0 }}>
                <strong>{candidateLabel(c)}</strong>
                <span style={{ color: colors.textSecondary }}>
                  {" · "}{c.source}
                  {c.record_id ? ` · record ${c.record_id}` : ""}
                </span>
                {c.computed_note && (
                  <span data-lot-computed style={{ display: "block", fontSize: 12, color: colors.textSecondary }}>
                    Computed: {c.computed_note}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
